import React, { useState, useRef, useLayoutEffect, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    Layout, HelpCircle, ImageIcon, Download, Search, Route, ClipboardList, User, Briefcase,
    ChevronDown, Filter, AlertTriangle, FilePlus, Trash2, Undo2, Redo2, UploadCloud, Link2,
    BarChart3, Eye, GitMerge, BookOpen, Save, Boxes,
} from 'lucide-react';
import { useGraphStore } from '../../stores/useGraphStore';
import { detectInputType, omnibarPickerChains, omnibarQueryIdentity, omnibarShownChain } from '../../utils/WorkbenchSearch';
import { ToolbarButton, ToolbarGroup, ToolbarDivider, TOOLBAR_FOCUS } from './WorkbenchToolbar';
import { GraphFilterBar } from './GraphFilterBar';
import { HiddenByTierNotice } from './HiddenByTierNotice';
import type { WorkbenchDataMode } from '../../utils/workbenchDataMode';
import { workbenchHeaderShell } from './workbenchStyles';

/** Undo/Redo — subscribe to graph store history stacks. */
const UndoRedoButtons: React.FC = () => {
    const undoAvailable = useGraphStore(s => s._past.length > 0);
    const redoAvailable = useGraphStore(s => s._future.length > 0);

    return (
        <div className="flex items-center gap-0.5" role="group" aria-label="Canvas history">
            <ToolbarButton
                variant="icon"
                onClick={() => useGraphStore.getState().undo()}
                disabled={!undoAvailable}
                icon={<Undo2 />}
                title="Undo last canvas change (Ctrl+Z)"
                aria-label="Undo"
            />
            <ToolbarButton
                variant="icon"
                onClick={() => useGraphStore.getState().redo()}
                disabled={!redoAvailable}
                icon={<Redo2 />}
                title="Redo canvas change (Ctrl+Shift+Z)"
                aria-label="Redo"
            />
        </div>
    );
};

interface WorkbenchHeaderProps {
    // Context Info
    caseId?: string | null;
    /** Return-path to the Fusion box canvas (Workbench → Fusion). Button hidden when absent. */
    onOpenFusion?: () => void;
    investigationId?: string | null;
    officerName?: string | null;
    elementsCount: number;

    // Core Omnibar state
    traceInput: string;
    onTraceInputChange: (val: string) => void;
    onPrimaryAction: (type: string, chain: string | null, isTx: boolean) => void | Promise<void>;
    /** URL/session chain so the EVM select hydrates (e.g. ?chain=BASE) instead of always showing ETH. */
    sessionChain?: string | null;

    // Feature Checks
    hasSession: boolean;
    hasElements: boolean;
    showHelp: boolean;

    // Graph Config Options
    showLegend: boolean;

    // Actions
    onAutoTrace: () => void | Promise<void>;
    onToggleAnalyze: () => void;
    onToggleAutoInvestigate: () => void;
    onToggleLegend: () => void;
    onSaveState: () => void | Promise<void>;
    onExportPng: () => void;
    onExportCsv: () => void;
    onExportExcel?: () => void;
    onToggleHelp: () => void;
    onToggleFilter?: () => void;
    showFilter?: boolean;
    caseName?: string | null;
    investigationName?: string | null;
    hasUnsavedChanges?: boolean;

    // Canvas management (Phase B)
    canvasNodeCount?: number;
    onNewInvestigation?: () => void;
    onClearCanvas?: () => void;
    onShowAllNodes?: () => void;
    onImportCsv?: () => void;
    onCheckConnections?: () => void;
    isCheckingConnections?: boolean;
    /** Number of nodes currently multi-selected; when >= 2, Links runs only on selection. */
    selectedCount?: number;

    // Demo Guide (shown only when a demo is loaded)
    showDemoGuideButton?: boolean;
    demoGuideOpen?: boolean;
    onToggleDemoGuide?: () => void;

    /** Mock / Cached / Live data source (session-scoped). */
    dataMode?: WorkbenchDataMode;
    onDataModeChange?: (mode: WorkbenchDataMode) => void;
}

export const WorkbenchHeader: React.FC<WorkbenchHeaderProps> = ({
    caseId,
    investigationId: _investigationId,
    onOpenFusion,
    officerName,
    elementsCount,
    traceInput,
    onTraceInputChange,
    onPrimaryAction,
    sessionChain = null,
    hasSession,
    hasElements,
    showHelp: _showHelp,
    showLegend,
    onAutoTrace,
    onToggleAnalyze,
    onToggleAutoInvestigate,
    onToggleLegend,
    onSaveState,
    onExportPng,
    onExportCsv,
    onExportExcel,
    onToggleHelp,
    onToggleFilter,
    showFilter,
    caseName,
    investigationName,
    hasUnsavedChanges,
    canvasNodeCount,
    onNewInvestigation,
    onClearCanvas,
    onShowAllNodes,
    onImportCsv,
    onCheckConnections,
    isCheckingConnections,
    selectedCount = 0,
    showDemoGuideButton,
    demoGuideOpen,
    onToggleDemoGuide,
    dataMode = 'live',
    onDataModeChange,
}) => {
    const detection = detectInputType(traceInput);
    const [pickedChain, setPickedChain] = useState<string | null>(null);
    const detectionQuery = detection.query || traceInput;
    const queryIdentityRef = useRef<string | null>(null);
    useEffect(() => {
        const identity = omnibarQueryIdentity(detectionQuery);
        if (queryIdentityRef.current !== null && queryIdentityRef.current !== identity) {
            setPickedChain(null);
        }
        queryIdentityRef.current = identity;
    }, [detectionQuery]);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);
    const [filterPanelTop, setFilterPanelTop] = useState(0);
    const shownChain = omnibarShownChain(pickedChain, sessionChain, detection.chain);
    const pickerChains = omnibarPickerChains(detection);

    const measureFilterPanelTop = useCallback(() => {
        if (headerRef.current) {
            setFilterPanelTop(headerRef.current.getBoundingClientRect().bottom + 6);
        }
    }, []);

    useLayoutEffect(() => {
        if (!showFilter) return;
        measureFilterPanelTop();
        window.addEventListener('resize', measureFilterPanelTop);
        return () => window.removeEventListener('resize', measureFilterPanelTop);
    }, [showFilter, measureFilterPanelTop, hasElements, hasSession]);

    const linksDisabled =
        isCheckingConnections || (selectedCount < 2 && (!canvasNodeCount || canvasNodeCount < 2));
    const linksTitle = isCheckingConnections
        ? 'Searching for paths between addresses on the canvas…'
        : selectedCount >= 2
            ? `Find direct or multi-hop paths between the ${selectedCount} selected addresses (L)`
            : canvasNodeCount && canvasNodeCount >= 2
                ? 'Find connections between addresses already on the canvas (prioritizes unlinked nodes) (L)'
                : 'Add at least two addresses to the canvas, or select two or more nodes (L)';

    const holisticTitle =
        selectedCount >= 2
            ? `Run a combined trace across ${selectedCount} selected addresses to find shared relays and cross-links`
            : 'Select two or more addresses on the canvas to run a combined multi-seed trace';

    return (
        <div
            ref={headerRef}
            className={`w-full relative z-40 flex flex-col gap-2 px-3 py-2 shrink-0 ${workbenchHeaderShell}`}
            data-testid="wb-header"
        >
            {/* Row 1: Omnibar + right-side utilities */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-3">
                <div className="flex flex-1 min-w-0 items-center gap-3 w-full">
                    <div className="hidden md:block select-none shrink-0">
                        <h1 className="text-sm font-semibold text-foreground tracking-tight">Workbench</h1>
                    </div>

                    {/* Smart Omnibar — flex row so the chain badge never paints over the address text */}
                    <div className="relative flex-1 min-w-0 max-w-2xl flex items-center" data-tour="omnibar">
                    <div
                        className="flex w-full min-w-0 items-center gap-1.5 rounded-md pl-3 pr-1 py-1 bg-background border border-input/60 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-colors"
                    >
                        <div className="text-muted-foreground pointer-events-none shrink-0" aria-hidden>
                            <Search className="w-4 h-4" />
                        </div>

                        <input
                            type="search"
                            name="workbench-omnibar"
                            value={traceInput}
                            onChange={(e) => onTraceInputChange(e.target.value)}
                            placeholder="Paste wallet, TX hash, or UPI ID…"
                            // Block browser postal-address autofill and password-manager
                            // injection. Chromium ignores bare autocomplete="off" when
                            // the placeholder/label looks like an address field.
                            autoComplete="one-time-code"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            data-1p-ignore="true"
                            data-lpignore="true"
                            data-bitwarden-no-filtering="true"
                            data-form-type="other"
                            className={`min-w-0 flex-1 bg-transparent border-0 py-1 text-sm outline-none shadow-none focus:ring-0 ${TOOLBAR_FOCUS}`}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && traceInput.trim()) {
                                    onPrimaryAction(detection.type, pickedChain, detection.isTx);
                                }
                            }}
                            data-testid="wb-omnibar-input"
                            aria-label="Search wallet, transaction hash, or UPI ID"
                        />

                        {traceInput.trim() && detection.label && (
                            <div
                                className="flex shrink-0 items-center gap-1 max-w-[50%] sm:max-w-[54%]"
                                data-tour="omnibar-chain-badge"
                                data-testid="wb-omnibar-chain-badge"
                            >
                                <span
                                    className={`truncate px-1.5 py-0.5 text-[10px] uppercase font-medium rounded tracking-wide border
                                    ${detection.type === 'unknown'
                                        ? 'bg-muted text-muted-foreground border-border'
                                        : detection.isTx
                                            ? 'bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/25'
                                            : 'bg-primary/10 text-primary border-primary/25'
                                    }`}
                                    title={[
                                        detection.label,
                                        pickerChains ? pickerChains.join('/') : null,
                                    ].filter(Boolean).join(' · ')}
                                >
                                    {detection.label}
                                </span>
                                {pickerChains && (
                                    <select
                                        className="h-6 max-w-[5.5rem] shrink-0 rounded border border-primary/25 bg-primary/10 px-1 text-[10px] uppercase font-medium tracking-wide text-primary outline-none focus:ring-2 focus:ring-primary/20"
                                        value={shownChain || pickerChains[0]}
                                        onChange={(e) => setPickedChain(e.target.value)}
                                        aria-label="Select chain for this EVM address"
                                        data-testid="wb-omnibar-chain-select"
                                        title="Same 0x address can be a different entity on each EVM chain. Pick the chain from the test case or investigation."
                                    >
                                        {pickerChains.map((c) => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        )}

                        <ToolbarButton
                            variant="primary"
                            onClick={() => onPrimaryAction(detection.type, pickedChain, detection.isTx)}
                            disabled={!traceInput.trim()}
                            icon={<Search />}
                            label="Add"
                            labelFrom="md"
                            className="shrink-0 h-8 px-2.5"
                            title="Add this address or transaction to the canvas"
                            aria-label="Add to canvas"
                        />
                    </div>
                </div>

                    {/* Filter toggle — sits directly beside the omnibar search */}
                    {hasElements && onToggleFilter && (
                        <div className="relative shrink-0">
                            <ToolbarButton
                                variant="outline"
                                active={!!showFilter}
                                onClick={onToggleFilter}
                                icon={<Filter />}
                                label="Filters"
                                labelFrom="md"
                                data-tour="header-filters"
                                title={showFilter ? 'Close graph filters (F)' : 'Filter visible addresses and transfers on the canvas (F)'}
                                aria-pressed={!!showFilter}
                                aria-expanded={!!showFilter}
                                aria-haspopup="dialog"
                            />
                        </div>
                    )}
                    {hasElements && <HiddenByTierNotice className="shrink-0" />}
                </div>

                {/* Right: Meta info & utilities */}
                <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                {showDemoGuideButton && onToggleDemoGuide && (
                    <ToolbarButton
                        variant="secondary"
                        active={!!demoGuideOpen}
                        onClick={onToggleDemoGuide}
                        aria-pressed={!!demoGuideOpen}
                        icon={<BookOpen />}
                        label="Demo Guide"
                        labelFrom="md"
                        title={
                            demoGuideOpen
                                ? 'Hide the step-by-step walkthrough for this demo scenario'
                                : 'Show a step-by-step walkthrough for this demo scenario'
                        }
                    />
                )}

                {(caseName || investigationName) ? (
                    <div className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground border border-border rounded-md px-2 py-1 max-w-[240px] bg-background/60">
                        <Briefcase className="w-3.5 h-3.5 shrink-0" />
                        <span className="font-medium text-foreground truncate">{caseName || 'Unnamed Case'}</span>
                        {investigationName && (
                            <>
                                <span className="text-border">/</span>
                                <span className="truncate">{investigationName}</span>
                            </>
                        )}
                        {hasUnsavedChanges && (
                            <span
                                className="text-amber-600 dark:text-amber-400 font-semibold text-sm leading-none ml-0.5"
                                title="Unsaved changes on canvas"
                            >
                                *
                            </span>
                        )}
                    </div>
                ) : (hasElements && hasUnsavedChanges) ? (
                    <div className="hidden md:flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400 bg-amber-500/10 px-2 py-1 rounded-md border border-amber-500/20">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        <span className="font-medium">Unsaved</span>
                    </div>
                ) : null}

                {caseId && onOpenFusion ? (
                    <button
                        type="button"
                        onClick={onOpenFusion}
                        data-tour="wb-open-fusion"
                        title="Open this case in the Fusion canvas (high-level story)"
                        aria-label="Open in Fusion canvas"
                        className="hidden md:flex items-center gap-1.5 text-xs border border-border rounded-md px-2 py-1 bg-background/60 hover:bg-secondary/60 transition-colors"
                    >
                        <Boxes className="w-3.5 h-3.5 shrink-0" />
                        <span className="font-medium">Fusion</span>
                    </button>
                ) : null}

                <ToolbarButton
                    variant="secondary"
                    active={showLegend}
                    onClick={onToggleLegend}
                    icon={<HelpCircle />}
                    label="Legend"
                    labelFrom="lg"
                    data-tour="header-legend"
                    title={showLegend ? 'Hide graph legend' : 'Show graph legend — colours, shapes, and edge styles'}
                    aria-pressed={showLegend}
                    aria-label="Toggle graph legend"
                />

                <ToolbarButton
                    variant="secondary"
                    onClick={onSaveState}
                    disabled={elementsCount === 0 || !hasUnsavedChanges}
                    icon={<Save />}
                    label={caseId ? 'Save Snapshot' : 'Save as New'}
                    labelFrom="md"
                    title={
                        caseId
                            ? 'Save the current canvas state to this investigation (Ctrl+S)'
                            : 'Save the canvas as a new case and investigation (Ctrl+S)'
                    }
                />

                <div className="relative">
                    <ToolbarButton
                        variant="icon"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        icon={<Layout />}
                        title="View options — legend, export, import"
                        aria-label="View options"
                        aria-expanded={isMenuOpen}
                        aria-haspopup="menu"
                    >
                        <ChevronDown className="w-3 h-3 opacity-60" />
                    </ToolbarButton>

                    {isMenuOpen && (
                        <div
                            className="absolute right-0 top-full mt-1 w-64 bg-popover border border-border rounded-md shadow-overlay py-1 z-50 animate-in fade-in zoom-in-95"
                            role="menu"
                        >
                            <div className="px-1 py-1 border-b border-border">
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() => { onToggleLegend(); setIsMenuOpen(false); }}
                                    className={`w-full flex items-center justify-between px-2 py-1.5 text-sm rounded hover:bg-accent ${TOOLBAR_FOCUS}`}
                                >
                                    <span className="flex items-center gap-2">
                                        <HelpCircle className="w-4 h-4 text-muted-foreground" />
                                        Legend
                                    </span>
                                    {showLegend && (
                                        <span className="text-[10px] bg-primary/15 text-primary px-1.5 py-0.5 rounded font-medium">
                                            On
                                        </span>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() => { onToggleHelp(); setIsMenuOpen(false); }}
                                    className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-accent text-left ${TOOLBAR_FOCUS}`}
                                >
                                    <HelpCircle className="w-4 h-4 text-muted-foreground" />
                                    Keyboard shortcuts
                                </button>
                            </div>

                            <div className="px-1 py-1">
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() => { onExportPng(); setIsMenuOpen(false); }}
                                    disabled={elementsCount === 0}
                                    className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-accent disabled:opacity-50 text-left ${TOOLBAR_FOCUS}`}
                                >
                                    <ImageIcon className="w-4 h-4 text-muted-foreground" />
                                    Export PNG
                                </button>
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() => { onExportCsv(); setIsMenuOpen(false); }}
                                    disabled={elementsCount === 0}
                                    className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-accent disabled:opacity-50 text-left ${TOOLBAR_FOCUS}`}
                                >
                                    <Download className="w-4 h-4 text-muted-foreground" />
                                    Export data (CSV)
                                </button>
                                {onExportExcel && (
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={() => { onExportExcel(); setIsMenuOpen(false); }}
                                        disabled={elementsCount === 0}
                                        className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-accent disabled:opacity-50 text-left ${TOOLBAR_FOCUS}`}
                                    >
                                        <Download className="w-4 h-4 text-muted-foreground" />
                                        Export data (Excel)
                                    </button>
                                )}
                                {onImportCsv && (
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={() => { onImportCsv(); setIsMenuOpen(false); }}
                                        className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-accent text-left ${TOOLBAR_FOCUS}`}
                                    >
                                        <UploadCloud className="w-4 h-4 text-muted-foreground" />
                                        Import addresses (CSV)
                                    </button>
                                )}
                            </div>

                            {(caseId || officerName) && (
                                <div className="px-3 py-2 bg-muted/50 border-t border-border mt-1">
                                    {caseId && (
                                        <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                                            <Briefcase className="w-3 h-3" />
                                            Case: {caseId}
                                        </div>
                                    )}
                                    {officerName && (
                                        <div className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                                            <User className="w-3 h-3" />
                                            IO: {officerName}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                </div>
            </div>

            {/* Row 2: Canvas utilities (left) + investigation actions & filters (right) */}
            {(hasElements || hasSession) && (
                <div
                    className="flex items-center justify-between gap-2 flex-wrap border-t border-border/50 pt-2 pb-0.5"
                    data-testid="wb-header-actions"
                >
                    {hasElements ? (
                        <div
                            className="flex items-center gap-1 shrink-0 rounded-md border border-border bg-muted/50 px-1 py-0.5"
                            role="group"
                            aria-label="Canvas utilities"
                        >
                            <UndoRedoButtons />
                            {canvasNodeCount != null && canvasNodeCount > 0 && (
                                <span
                                    className="text-[11px] text-muted-foreground tabular-nums px-1.5 py-0.5 min-w-[1.75rem] text-center"
                                    title={`${canvasNodeCount} address${canvasNodeCount !== 1 ? 'es' : ''} on canvas`}
                                >
                                    {canvasNodeCount}
                                </span>
                            )}
                            {onNewInvestigation && (
                                <ToolbarButton
                                    variant="icon"
                                    onClick={onNewInvestigation}
                                    icon={<FilePlus />}
                                    title="Start a new investigation on a blank canvas (Ctrl+Alt+N)"
                                    aria-label="New investigation"
                                />
                            )}
                            {onClearCanvas && (
                                <ToolbarButton
                                    variant="destructive"
                                    onClick={onClearCanvas}
                                    icon={<Trash2 />}
                                    title="Remove all nodes and edges from the canvas (the case, investigation and search input are kept)"
                                    aria-label="Clear canvas"
                                />
                            )}
                            {onShowAllNodes && (
                                <ToolbarButton
                                    variant="icon"
                                    onClick={onShowAllNodes}
                                    icon={<Eye />}
                                    title="Reset visibility and show every node on the canvas"
                                    aria-label="Show all nodes"
                                />
                            )}
                        </div>
                    ) : (
                        <div className="shrink-0" aria-hidden />
                    )}

                    <div className="flex items-center gap-2 ml-auto shrink-0 flex-wrap justify-end">
                        {hasSession && (
                            <>
                                <ToolbarGroup label="Graph tracing">
                                    <ToolbarButton
                                        grouped
                                        variant="outline"
                                        onClick={onAutoTrace}
                                        icon={<Route />}
                                        label="Trace"
                                        title="Follow the Money — trace the selected address to the nearest KYC exchange (T)"
                                        data-tour="header-trace"
                                    />
                                    {onCheckConnections && (
                                        <ToolbarButton
                                            grouped
                                            variant="outline"
                                            onClick={onCheckConnections}
                                            disabled={linksDisabled}
                                            icon={<Link2 />}
                                            label={
                                                isCheckingConnections
                                                    ? 'Finding…'
                                                    : selectedCount >= 2
                                                        ? `Links (${selectedCount})`
                                                        : 'Links'
                                            }
                                            title={linksTitle}
                                            data-tour="header-links"
                                        />
                                    )}
                                    <ToolbarButton
                                        grouped
                                        variant="outline"
                                        onClick={() => onPrimaryAction('holistic', shownChain, false)}
                                        disabled={selectedCount < 2}
                                        icon={<GitMerge />}
                                        label="Holistic"
                                        title={holisticTitle}
                                    />
                                </ToolbarGroup>

                                <ToolbarDivider />

                                <ToolbarGroup label="Analysis">
                                    <ToolbarButton
                                        grouped
                                        variant="primary"
                                        onClick={onToggleAutoInvestigate}
                                        icon={<ClipboardList />}
                                        label="Investigate"
                                        title="Open Targeted Node Intelligence — 8-step forensic analysis in the Node Inspector Analysis tab (I)"
                                        data-tour="header-investigate"
                                    />
                                    {hasElements && (
                                        <ToolbarButton
                                            grouped
                                            variant="outline"
                                            onClick={onToggleAnalyze}
                                            icon={<BarChart3 />}
                                            label="Analyze"
                                            title="Open Macro Graph Analysis — pattern scan, algorithms, and summary for the whole graph (G)"
                                            data-tour="header-analyze"
                                        />
                                    )}
                                </ToolbarGroup>
                            </>
                        )}

                    </div>
                </div>
            )}

            {(isMenuOpen || showFilter) && (
                <div
                    className="fixed inset-0 z-30"
                    onClick={() => {
                        setIsMenuOpen(false);
                        if (showFilter && onToggleFilter) onToggleFilter();
                    }}
                    aria-hidden
                />
            )}

            {showFilter && onToggleFilter && createPortal(
                <div
                    className="fixed left-1/2 -translate-x-1/2 z-50 w-[min(calc(100vw-2rem),28rem)] min-w-[18rem] flex flex-col min-h-0 overflow-hidden"
                    style={{
                        top: filterPanelTop,
                        maxHeight: filterPanelTop > 0 ? `calc(100vh - ${filterPanelTop}px - 1rem)` : 'min(75vh, 640px)',
                    }}
                    data-testid="wb-filters-panel"
                    role="dialog"
                    aria-label="Graph filters"
                >
                    <GraphFilterBar variant="popover" onClose={onToggleFilter} />
                </div>,
                document.body,
            )}
        </div>
    );
};
